import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import striptags from 'striptags';
import get from 'lodash/get';
import { getImageByWidth } from 'api';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { IndexNav } from 'msp-components';
import Error404 from 'components/Error404';
import Wrapper from 'components/Wrapper';
import Title from 'components/Title';

const PlayerWrapper = ({ project, menuItems, templateOptions, items, history, location }) => {
  const [containerHeight, setContainerHeight] = useState();
  const handleResize = (width, height) => setContainerHeight(height);

  const navItems = useMemo(() => (
    items.ordered.map(id => {
      const item = items.byId[id];
      const pi = getImageByWidth(get(item, 'previewImages.horizontalThumbnail', []), 200);
      return {
        id,
        thumbnail: pi ? pi.url : null,
        title: item.name,
        slug: item.urlSlug,
        description: striptags(item.shortDescription),
      };
    })
  ), [items]);

  const titlePaths = navItems.reduce((result, item, idx) => {
    result[item.id] = `/${idx + 1}/${item.slug}`;
    return result;
  }, {});
  const firstItemPath = titlePaths[items.ordered[0]];

  const activeItemId = Object.keys(titlePaths).find(key => location.pathname.startsWith(titlePaths[key]));
  const handleItemSelect = id => history.push(titlePaths[id]);

  const routes = (
    <Route render={({ location }) => {
      // Remove trailing slash, preserving query params
      if (location.pathname.endsWith('/') && location.pathname !== '/') {
        return <Redirect to={location.pathname.slice(0, -1) + location.search} />;
      }

      // Check for global error state
      return location.state && location.state.is404 ? <Error404 /> : (
        <Switch>
          <Route exact path="/" render={props => <Redirect to={firstItemPath} />} />
          {Object.entries(titlePaths).map(([id, path]) => (
            <Route key={id} path={path} render={props => <Title routeProps={props} project={project} menuItems={menuItems} data={items.byId[id]} />} />
          ))}
          <Route component={Error404} />
        </Switch>
      );
    }} />
  );

  const navigation = navItems.length > 1 && (
    <IndexNav
      menuHeight={containerHeight ? containerHeight - 125 : 500}
      items={navItems}
      activeItemId={activeItemId}
      onSelectItem={handleItemSelect}
    />
  );

  return (
    <Wrapper
      project={project}
      templateOptions={templateOptions}
      navigation={navigation}
      titlePaths={titlePaths}
      onContainerResize={handleResize}
    >
      {routes}
    </Wrapper>
  );
};

PlayerWrapper.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    content: PropTypes.string,
    isVisible: PropTypes.bool,
  })),
  templateOptions: PropTypes.shape({
    accentColor: PropTypes.string,
    logo: PropTypes.string,
    theme: PropTypes.oneOf(['light', 'dark']),
    footerText: PropTypes.string,
    sponsorshipText: PropTypes.string,
    sponsorshipLink: PropTypes.string,
    sponsorshipImage: PropTypes.string,
  }),
  project: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    type: PropTypes.string,
    shortDescription: PropTypes.string,
    longDescription: PropTypes.string,
    socialImage: PropTypes.string,
    analyticsProvider: PropTypes.string,
    analyticsId: PropTypes.string,
    shareUrl: PropTypes.string,
    publisher: PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      twitter: PropTypes.string,
      facebook: PropTypes.string,
    }),
  }),
  items: PropTypes.shape({
    byId: PropTypes.object,
    ordered: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default withRouter(PlayerWrapper);
